import React, { FC, useMemo } from "react";

import { getTranslationLabelLanguage } from "../helpers/loadTranslationLabels";
import Stack from "@mui/material/Stack";
import { Column, Locales, QuestionType, Nullable, isVariantActive, TicketingFormCategory } from "@simplyk/common";
import { Control, FieldErrors, RegisterOptions, UseFormSetValue, useFormContext } from "react-hook-form";
import { v4 } from "uuid";

import { ReminderTranslationDefaults } from "../helpers/reminderTranslations";
import { TicketingEmailTranslationText } from "../helpers/emailTranslationText";
import { CommunicationPreferencesTitleTranslation } from "../sharedComponents/CommunicationPreferencesTitleTranslation";
import { TranslatedFieldWysiwyg } from "../sharedComponents/TranslatedFieldWysiwyg";
import { TranslationFieldWysiwyg } from "../sharedComponents/TranslationFieldWysiwyg";
import { TranslationSection } from "../sharedComponents/TranslationSection";
import { TranslationLocaleProvider, useTranslationLocales } from "../Context/TranslationLocaleContext";
import { TranslatedTextField } from "../sharedComponents/TranslatedTextField";
import { useTranslationSwapVersion } from "../sharedComponents/TranslationSwapContext";
import { ShopVariantTranslationFieldData, TicketingTranslationsData } from "../translation.types";

import { buildPrizesToTranslate } from "./PrizeTranslationSection/helpers/buildPrizesToTranslate";
import { PrizeTranslationSection } from "./PrizeTranslationSection/PrizeTranslationSection";
import { RatesTranslationCard } from "./RateTranslationCard";
import { ShopVariantTranslationCard } from "./ShopVariantTranslationCard/ShopVariantTranslationCard";
import { useTranslatePrizes } from "./useTranslatePrizes";
import { useTranslateQuestions } from "./useTranslateQuestions";
import { useTranslateRates } from "./useTranslateRates";

import { isRequired } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import { ChoiceOutput, TicketingOutput } from "@/common/types/trpc";
import { FroalaConfig } from "@/components/OutlinedTextFieldWysiwyg/type";
import { TranslationLine } from "@/components/TranslationLine/TranslationLine";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { QuestionTranslationCard } from "@/features/LiveFormEditor/Steps/StepAdvancedSettings/Translations/sharedComponents/QuestionTranslationCard";

const nullFields = {
  id: undefined,
  title: undefined,
  description: undefined,
  chequeDescription: undefined,
  offlinePaymentWording: undefined,
  communicationPreferencesTitle: undefined,
};

const getQuestionChoices = (choices: ChoiceOutput[], locale: Locales) => {
  return choices.map((choice) => ({
    id: choice.id,
    value: choice.choiceFields?.find((field) => field.locale === locale)?.body ?? "",
    sortIndex: choice.sortIndex,
  }));
};

type ShopVariantOutput = {
  id: string;
  shopVariantFields?: Array<{ locale: string; name: string }>;
  shopVariantValues?: Array<{
    id: string;
    shopVariantValueFields?: Array<{ locale: string; name: string }>;
  }> | null;
};

const buildShopVariantTranslationFieldsByRate = (
  rates: TicketingOutput["rates"],
  locale: Locales
): Map<string, ShopVariantTranslationFieldData[]> => {
  const map = new Map<string, ShopVariantTranslationFieldData[]>();
  if (!rates) {
    return map;
  }

  for (const rate of rates) {
    const shopVariants = rate.shopVariants as ShopVariantOutput[] | undefined;
    if (!shopVariants?.length) {
      continue;
    }

    const rateVariants: ShopVariantTranslationFieldData[] = [];
    for (const variant of shopVariants) {
      const variantField = variant.shopVariantFields?.find((field) => field.locale === locale);
      const name = variantField?.name || "";

      const values =
        variant.shopVariantValues?.map((value) => {
          const valueField = value.shopVariantValueFields?.find((field) => field.locale === locale);
          return {
            valueId: value.id,
            name: valueField?.name || "",
          };
        }) || [];

      rateVariants.push({ variantId: variant.id, name, values });
    }

    if (rateVariants.length > 0) {
      map.set(rate.id, rateVariants);
    }
  }

  return map;
};

export interface TicketingTranslationColumnProps {
  data: TicketingOutput;
  formLocale: Locales;
  translationLocale: Locales;
  isAuction: boolean;
  reminders?: ReminderTranslationDefaults[];
  emailText: { fields: TicketingEmailTranslationText; translations: TicketingEmailTranslationText };
  communicationPreferencesTitle: Nullable<string>;
  onTranslationStateChange?: (isTranslating: boolean) => void;
}

export const TicketingTranslationColumn: FC<TicketingTranslationColumnProps> = ({
  data,
  formLocale,
  translationLocale,
  isAuction,
  reminders,
  emailText,
  communicationPreferencesTitle,
  onTranslationStateChange,
}) => {
  const { control, formState, register, setValue } = useFormContext<TicketingTranslationsData>();

  const { t } = useTranslate();
  const hasDate = Boolean(data.recurrences?.length);
  const defaultFields = data?.ticketingFields?.find((field) => field.locale === formLocale) || nullFields;
  const defaultTranslationFields =
    data?.ticketingFields?.find((field) => field.locale === translationLocale) || nullFields;

  const { title, description } = defaultFields;
  const { showChequeDescription, showOfflinePaymentWording } = getPaymentInstructionsVisibility(
    isAuction,
    defaultFields
  );
  const { fields: emailFields, translations: emailTranslations } = emailText;
  const { emailContent, emailObject, bidLoserEmailContent, bidLoserEmailObject } = emailFields;

  const fieldErrors = formState.errors[Column.Fields];
  const translationErrors = formState.errors[Column.Translations];

  const isRequiredRule = [isRequired(t("dashboard", "common.isRequired"))];
  const fieldSubjectRef = React.useRef<Nullable<HTMLInputElement>>(null);
  const loserSubjectRef = React.useRef<Nullable<HTMLInputElement>>(null);
  const bidPlacedSubjectRef = React.useRef<Nullable<HTMLInputElement>>(null);
  const bidPlacedTranslationSubjectRef = React.useRef<Nullable<HTMLInputElement>>(null);
  const translationSubjectRef = React.useRef<Nullable<HTMLInputElement>>(null);
  const loserTranslationSubjectRef = React.useRef<Nullable<HTMLInputElement>>(null);
  const expiringFieldSubjectRef = React.useRef<Nullable<HTMLInputElement>>(null);
  const expiringTranslationSubjectRef = React.useRef<Nullable<HTMLInputElement>>(null);
  const autoRenewalFieldSubjectRef = React.useRef<Nullable<HTMLInputElement>>(null);
  const autoRenewalTranslationSubjectRef = React.useRef<Nullable<HTMLInputElement>>(null);
  const readyForPickupFieldSubjectRef = React.useRef<Nullable<HTMLInputElement>>(null);
  const readyForPickupTranslationSubjectRef = React.useRef<Nullable<HTMLInputElement>>(null);
  const orderShippedFieldSubjectRef = React.useRef<Nullable<HTMLInputElement>>(null);
  const orderShippedTranslationSubjectRef = React.useRef<Nullable<HTMLInputElement>>(null);

  const sortedRates = useMemo(
    () => data.rates?.slice().sort((first, second) => first.sortIndex - second.sortIndex) ?? [],
    [data.rates]
  );
  const ratesToTranslate = useMemo(
    () =>
      sortedRates.map((rate) => {
        const rateField = rate.rateFields?.find((field) => field.locale === formLocale);
        return {
          id: rateField?.id || "",
          title: rateField?.title || "",
          description: rateField?.description || "",
          locale: formLocale,
        };
      }),
    [sortedRates, formLocale]
  );

  const { isTranslating: isTranslatingRates, translatedRateIds } = useTranslateRates({
    rates: ratesToTranslate,
    translationLocale,
    setValue,
    existingTranslations: sortedRates.map((rate) =>
      rate.rateFields?.find((field) => field.locale === translationLocale)
    ),
  });

  const sortedPrizes = useMemo(
    () => buildPrizesToTranslate(data.prizes, formLocale, translationLocale),
    [data.prizes, formLocale, translationLocale]
  );

  const { isTranslatingPrizes, translatedPrizeIds } = useTranslatePrizes({
    prizes: sortedPrizes,
    translationLocale,
    setValue,
  });

  const questionsToTranslate = useMemo(
    () =>
      data?.questions
        ?.filter((question) => question.type !== QuestionType.Name)
        .sort((first, second) => first.sortIndex - second.sortIndex) ?? [],
    [data?.questions]
  );

  const { isTranslating: isTranslatingQuestions, translatedQuestionFieldIds } = useTranslateQuestions({
    questions: questionsToTranslate,
    formLocale,
    translationLocale,
    setValue,
  });

  const hasVariants = isVariantActive(data.formCategory);

  const shopVariantFieldsByRateId = useMemo(
    () =>
      hasVariants
        ? buildShopVariantTranslationFieldsByRate(data.rates, formLocale)
        : new Map<string, ShopVariantTranslationFieldData[]>(),
    [hasVariants, data.rates, formLocale]
  );

  const shopVariantTranslationsByRateId = useMemo(
    () =>
      hasVariants
        ? buildShopVariantTranslationFieldsByRate(data.rates, translationLocale)
        : new Map<string, ShopVariantTranslationFieldData[]>(),
    [hasVariants, data.rates, translationLocale]
  );

  // Notify parent component when translation state changes
  React.useEffect(() => {
    const isTranslating = isTranslatingRates || isTranslatingPrizes || isTranslatingQuestions;
    onTranslationStateChange?.(isTranslating);
  }, [isTranslatingRates, isTranslatingPrizes, isTranslatingQuestions, onTranslationStateChange]);

  return (
    <TranslationLocaleProvider formLocale={formLocale} translationLocale={translationLocale}>
      <Stack gap={3}>
        <input style={{ display: "none" }} value={defaultFields.id} {...register(`${Column.Fields}.id`)} />
        <input
          style={{ display: "none" }}
          value={defaultTranslationFields.id}
          {...register(`${Column.Translations}.id`)}
        />

        {hasCampaignDetails(title, description) && (
          <TranslationSection title={t("dashboard", "translation.campaignDetails")}>
            {title && (
              <TranslationLine>
                <DashboardTextField
                  label={t("dashboard", "translation.campaignTitle", { lng: getTranslationLabelLanguage(formLocale) })}
                  rules={isRequiredRule}
                  control={control}
                  name={`${Column.Fields}.title`}
                  defaultValue={defaultFields.title}
                  error={Boolean(fieldErrors && fieldErrors["title"])}
                  errorMessage={fieldErrors && fieldErrors["title"]?.message}
                  data-test="ticketing-title-field"
                />
                <TranslatedTextField
                  fieldName={`${Column.Translations}.title`}
                  defaultFieldValue={defaultFields.title}
                  defaultTranslationFieldValue={defaultTranslationFields.title}
                  setValue={setValue}
                  translationLocale={translationLocale}
                  label={t("dashboard", "translation.campaignTitle", {
                    lng: getTranslationLabelLanguage(translationLocale),
                  })}
                  rules={isRequiredRule}
                  control={control}
                  error={Boolean(translationErrors && translationErrors["title"])}
                  errorMessage={translationErrors && translationErrors["title"]?.message}
                  dataTest="ticketing-title-translation"
                />
              </TranslationLine>
            )}
            {(description || description === "") && (
              <TranslationLine>
                <TranslationFieldWysiwyg
                  id={`${Column.Fields}.description`}
                  name={`${Column.Fields}.description`}
                  defaultValue={defaultFields.description}
                  keyName={`${Column.Fields}.description`}
                  heightMinOver310={400}
                  error={fieldErrors && fieldErrors["description"]}
                  config={FroalaConfig.Minimalist}
                  data-test="ticketing-description-field"
                />

                <TranslatedFieldWysiwyg
                  fieldName={`${Column.Translations}.description`}
                  defaultFieldValue={defaultFields.description}
                  defaultTranslationFieldValue={defaultTranslationFields.description}
                  setValue={setValue}
                  translationLocale={translationLocale}
                  subjectRef={fieldSubjectRef}
                  error={translationErrors && translationErrors["description"]}
                  config={FroalaConfig.Minimalist}
                  dataTest="ticketing-description-translation"
                />
              </TranslationLine>
            )}
          </TranslationSection>
        )}
        {Boolean(emailObject || emailContent) && (
          <TranslationSection
            title={t("dashboard", isAuction ? "translation.winnerEmail" : "translation.confirmationEmail")}
          >
            {emailObject && (
              <TranslationLine>
                <DashboardTextField
                  label={t("dashboard", "translation.subjectLine", { lng: getTranslationLabelLanguage(formLocale) })}
                  rules={isRequiredRule}
                  control={control}
                  name={`${Column.Fields}.emailObject`}
                  defaultValue={emailFields.emailObject}
                  error={Boolean(fieldErrors && fieldErrors["emailObject"])}
                  errorMessage={fieldErrors && fieldErrors["emailObject"]?.message}
                  inputProps={{
                    ref: fieldSubjectRef,
                  }}
                  data-test="ticketing-emailObject-field"
                />
                <TranslatedTextField
                  fieldName={`${Column.Translations}.emailObject`}
                  defaultFieldValue={emailFields.emailObject}
                  defaultTranslationFieldValue={emailTranslations.emailObject}
                  setValue={setValue}
                  translationLocale={translationLocale}
                  label={t("dashboard", "translation.subjectLine", {
                    lng: getTranslationLabelLanguage(translationLocale),
                  })}
                  rules={isRequiredRule}
                  control={control}
                  inputProps={{
                    ref: translationSubjectRef,
                  }}
                  data-test="ticketing-emailObject-translation"
                  error={Boolean(translationErrors && translationErrors["emailObject"])}
                  errorMessage={translationErrors && translationErrors["emailObject"]?.message}
                  dataTest="ticketing-emailObject-translation"
                />
              </TranslationLine>
            )}
            {emailContent && (
              <TranslationLine>
                <TranslationFieldWysiwyg
                  name={`${Column.Fields}.emailContent`}
                  subjectName={`${Column.Fields}.emailObject`}
                  subjectRef={fieldSubjectRef}
                  defaultValue={emailFields.emailContent}
                  attachmentKeyName={`${Column.Fields}.attachment`}
                  rules={isRequiredRule}
                  heightMinOver310={400}
                  error={fieldErrors && fieldErrors["emailContent"]}
                  data-test="ticketing-emailContent-field"
                  customConfig={{
                    date: hasDate,
                  }}
                  config={FroalaConfig.Email}
                />
                <TranslatedFieldWysiwyg
                  fieldName={`${Column.Translations}.emailContent`}
                  defaultFieldValue={emailFields.emailContent}
                  defaultTranslationFieldValue={emailTranslations.emailContent}
                  setValue={setValue}
                  translationLocale={translationLocale}
                  subjectRef={translationSubjectRef}
                  attachmentKeyName={`${Column.Translations}.attachment`}
                  rules={isRequiredRule}
                  error={translationErrors && translationErrors["emailContent"]}
                  dataTest="ticketing-emailContent-translation"
                  customConfig={{
                    date: hasDate,
                  }}
                  config={FroalaConfig.Email}
                />
              </TranslationLine>
            )}
          </TranslationSection>
        )}
        <BidPlacedEmailTranslationFields
          defaultFields={emailFields}
          defaultTranslationFields={emailTranslations}
          fieldErrors={fieldErrors}
          translationErrors={translationErrors}
          isRequiredRule={isRequiredRule}
          control={control}
          setValue={setValue}
          translationLocale={translationLocale}
          hasDate={hasDate}
          bidPlacedSubjectRef={bidPlacedSubjectRef}
          bidPlacedTranslationSubjectRef={bidPlacedTranslationSubjectRef}
        />
        {Boolean(bidLoserEmailObject || bidLoserEmailContent) && (
          <TranslationSection title={t("dashboard", "translation.loserEmail")}>
            {bidLoserEmailObject && (
              <TranslationLine>
                <DashboardTextField
                  label={t("dashboard", "translation.subjectLine", { lng: getTranslationLabelLanguage(formLocale) })}
                  rules={isRequiredRule}
                  control={control}
                  name={`${Column.Fields}.bidLoserEmailObject`}
                  defaultValue={emailFields.bidLoserEmailObject}
                  error={Boolean(fieldErrors && fieldErrors["bidLoserEmailObject"])}
                  errorMessage={fieldErrors && fieldErrors["bidLoserEmailObject"]?.message}
                  inputProps={{
                    ref: loserSubjectRef,
                  }}
                  data-test="ticketing-bidLoserEmailObject-field"
                />
                <TranslatedTextField
                  fieldName={`${Column.Translations}.bidLoserEmailObject`}
                  defaultFieldValue={emailFields.bidLoserEmailObject}
                  defaultTranslationFieldValue={emailTranslations.bidLoserEmailObject}
                  setValue={setValue}
                  translationLocale={translationLocale}
                  label={t("dashboard", "translation.subjectLine", {
                    lng: getTranslationLabelLanguage(translationLocale),
                  })}
                  rules={isRequiredRule}
                  control={control}
                  inputProps={{
                    ref: loserTranslationSubjectRef,
                  }}
                  data-test="ticketing-bidLoserEmailObject-translation"
                  error={Boolean(translationErrors && translationErrors["bidLoserEmailObject"])}
                  errorMessage={translationErrors && translationErrors["bidLoserEmailObject"]?.message}
                  dataTest="ticketing-bidLoserEmailObject-translation"
                />
              </TranslationLine>
            )}
            {bidLoserEmailContent && (
              <TranslationLine>
                <TranslationFieldWysiwyg
                  name={`${Column.Fields}.bidLoserEmailContent`}
                  subjectName={`${Column.Fields}.bidLoserEmailObject`}
                  subjectRef={loserSubjectRef}
                  defaultValue={emailFields.bidLoserEmailContent || ""}
                  rules={isRequiredRule}
                  heightMinOver310={400}
                  error={fieldErrors && fieldErrors["bidLoserEmailContent"]}
                  data-test="ticketing-bidLoserEmailContent-field"
                  customConfig={{
                    date: hasDate,
                  }}
                  config={FroalaConfig.Email}
                />
                <TranslatedFieldWysiwyg
                  fieldName={`${Column.Translations}.bidLoserEmailContent`}
                  defaultFieldValue={emailFields.bidLoserEmailContent}
                  defaultTranslationFieldValue={emailTranslations.bidLoserEmailContent}
                  setValue={setValue}
                  translationLocale={translationLocale}
                  subjectRef={loserTranslationSubjectRef}
                  rules={isRequiredRule}
                  error={translationErrors && translationErrors["bidLoserEmailContent"]}
                  dataTest="ticketing-bidLoserEmailContent-translation"
                  customConfig={{
                    date: hasDate,
                  }}
                  config={FroalaConfig.Email}
                />
              </TranslationLine>
            )}
          </TranslationSection>
        )}
        <RenewalEmailTranslationFields
          defaultFields={emailFields}
          defaultTranslationFields={emailTranslations}
          fieldErrors={fieldErrors}
          translationErrors={translationErrors}
          isRequiredRule={isRequiredRule}
          control={control}
          setValue={setValue}
          translationLocale={translationLocale}
          expiringFieldSubjectRef={expiringFieldSubjectRef}
          expiringTranslationSubjectRef={expiringTranslationSubjectRef}
          autoRenewalFieldSubjectRef={autoRenewalFieldSubjectRef}
          autoRenewalTranslationSubjectRef={autoRenewalTranslationSubjectRef}
        />
        <FulfillmentEmailTranslationFields
          defaultFields={emailFields}
          defaultTranslationFields={emailTranslations}
          fieldErrors={fieldErrors}
          translationErrors={translationErrors}
          isRequiredRule={isRequiredRule}
          control={control}
          setValue={setValue}
          translationLocale={translationLocale}
          readyForPickupFieldSubjectRef={readyForPickupFieldSubjectRef}
          readyForPickupTranslationSubjectRef={readyForPickupTranslationSubjectRef}
          orderShippedFieldSubjectRef={orderShippedFieldSubjectRef}
          orderShippedTranslationSubjectRef={orderShippedTranslationSubjectRef}
        />
        {reminders && reminders.length > 0 && (
          <ReminderEmailTranslationFields
            reminders={reminders}
            fieldErrors={fieldErrors}
            translationErrors={translationErrors}
            isRequiredRule={isRequiredRule}
            control={control}
            setValue={setValue}
            translationLocale={translationLocale}
            hasDate={hasDate}
          />
        )}
        {(showChequeDescription || showOfflinePaymentWording) && (
          <TranslationSection title={t("dashboard", "translation.paymentInstructions")}>
            {showChequeDescription && (
              <TranslationLine>
                <DashboardTextField
                  label={t("dashboard", "translation.chequeDescription", {
                    lng: getTranslationLabelLanguage(formLocale),
                  })}
                  rules={isRequiredRule}
                  control={control}
                  name={`${Column.Fields}.chequeDescription`}
                  defaultValue={defaultFields.chequeDescription || undefined}
                  error={Boolean(fieldErrors && fieldErrors["chequeDescription"])}
                  errorMessage={fieldErrors && fieldErrors["chequeDescription"]?.message}
                  multiline
                  rows={4}
                  maxRows={8}
                  data-test="ticketing-cheque-field"
                />
                <TranslatedTextField
                  fieldName={`${Column.Translations}.chequeDescription`}
                  defaultFieldValue={defaultFields.chequeDescription}
                  defaultTranslationFieldValue={defaultTranslationFields.chequeDescription}
                  setValue={setValue}
                  translationLocale={translationLocale}
                  label={t("dashboard", "translation.chequeDescription", {
                    lng: getTranslationLabelLanguage(translationLocale),
                  })}
                  rules={isRequiredRule}
                  control={control}
                  error={Boolean(translationErrors && translationErrors["chequeDescription"])}
                  errorMessage={translationErrors && translationErrors["chequeDescription"]?.message}
                  multiline
                  rows={4}
                  maxRows={8}
                  dataTest="ticketing-cheque-translation"
                />
              </TranslationLine>
            )}
            {showOfflinePaymentWording && (
              <TranslationLine>
                <DashboardTextField
                  label={t("dashboard", "translation.offlinePaymentWording", {
                    lng: getTranslationLabelLanguage(formLocale),
                  })}
                  inputProps={{ maxLength: 35 }}
                  rules={isRequiredRule}
                  control={control}
                  name={`${Column.Fields}.offlinePaymentWording`}
                  defaultValue={defaultFields.offlinePaymentWording}
                  error={Boolean(fieldErrors && fieldErrors["offlinePaymentWording"])}
                  errorMessage={fieldErrors && fieldErrors["offlinePaymentWording"]?.message}
                />
                <TranslatedTextField
                  fieldName={`${Column.Translations}.offlinePaymentWording`}
                  defaultFieldValue={defaultFields.offlinePaymentWording}
                  defaultTranslationFieldValue={defaultTranslationFields.offlinePaymentWording}
                  setValue={setValue}
                  translationLocale={translationLocale}
                  label={t("dashboard", "translation.offlinePaymentWording", {
                    lng: getTranslationLabelLanguage(translationLocale),
                  })}
                  inputProps={{ maxLength: 35 }}
                  rules={isRequiredRule}
                  control={control}
                  error={Boolean(translationErrors && translationErrors["offlinePaymentWording"])}
                  errorMessage={translationErrors && translationErrors["offlinePaymentWording"]?.message}
                  dataTest="ticketing-offlinePaymentWording-translation"
                />
              </TranslationLine>
            )}
          </TranslationSection>
        )}
        {Boolean(data.rates?.length) && (
          <TranslationSection title={t("dashboard", rateSectionLabels[data.formCategory])}>
            {(() => {
              let globalVariantIndex = 0;

              return sortedRates.map((rate, index) => {
                const defaultField = rate.rateFields?.find((field) => field.locale === formLocale);
                const defaultTranslationFields = rate.rateFields?.find((field) => field.locale === translationLocale);

                const rateVariantFields = shopVariantFieldsByRateId.get(rate.id) ?? [];
                const rateVariantTranslations = shopVariantTranslationsByRateId.get(rate.id) ?? [];

                const variantElements = rateVariantFields.map((variantField) => {
                  const variantIndex = globalVariantIndex++;
                  const translationVariant = rateVariantTranslations.find(
                    (translationField) => translationField.variantId === variantField.variantId
                  );

                  return (
                    <TranslationLine key={variantField.variantId}>
                      <ShopVariantTranslationCard
                        control={control}
                        setValue={setValue}
                        variantIndex={variantIndex}
                        fieldData={variantField}
                        translationData={variantField}
                        translationLocale={translationLocale}
                        isTranslationColumn={false}
                      />
                      <ShopVariantTranslationCard
                        control={control}
                        setValue={setValue}
                        variantIndex={variantIndex}
                        fieldData={variantField}
                        translationData={translationVariant}
                        translationLocale={translationLocale}
                        isTranslationColumn={true}
                      />
                    </TranslationLine>
                  );
                });

                return (
                  <React.Fragment key={defaultField?.id ?? rate.id}>
                    <TranslationLine>
                      <RatesTranslationCard
                        formCategory={data.formCategory}
                        index={index}
                        control={control}
                        register={register}
                        defaultValue={defaultField}
                        idFormName={`${Column.Fields}.ratesFields.${index}.id`}
                        titleFormName={`${Column.Fields}.ratesFields.${index}.title`}
                        descriptionFormName={`${Column.Fields}.ratesFields.${index}.description`}
                        amount={rate.amount}
                        titleErrorMessage={fieldErrors?.["ratesFields"]?.[index]?.["title"]?.message}
                        descriptionErrorMessage={fieldErrors?.["ratesFields"]?.[index]?.["description"]?.message}
                        isTranslationColumn={false}
                        translationLocale={translationLocale}
                        setValue={setValue}
                      />
                      <RatesTranslationCard
                        formCategory={data.formCategory}
                        index={index}
                        control={control}
                        register={register}
                        setValue={setValue}
                        defaultValue={defaultTranslationFields}
                        idFormName={`${Column.Translations}.ratesFields.${index}.id`}
                        titleFormName={`${Column.Translations}.ratesFields.${index}.title`}
                        descriptionFormName={`${Column.Translations}.ratesFields.${index}.description`}
                        amount={rate.amount}
                        titleErrorMessage={translationErrors?.["ratesFields"]?.[index]?.["title"]?.message}
                        descriptionErrorMessage={translationErrors?.["ratesFields"]?.[index]?.["description"]?.message}
                        translationLocale={translationLocale}
                        textToTranslate={defaultField}
                        isTranslationColumn={true}
                        isLoading={isTranslatingRates && !translatedRateIds.has(defaultField?.id ?? "")}
                      />
                    </TranslationLine>
                    {variantElements}
                  </React.Fragment>
                );
              });
            })()}
          </TranslationSection>
        )}

        <PrizeTranslationSection
          prizes={sortedPrizes}
          translationLocale={translationLocale}
          isTranslating={isTranslatingPrizes}
          translatedPrizeIds={translatedPrizeIds}
        />

        {questionsToTranslate.length > 0 && (
          <TranslationSection title={t("dashboard", "translation.questions")}>
            {questionsToTranslate.map((question, index) => {
              const defaultFields = question.questionFields?.find((field) => field.locale === formLocale);
              const defaultTranslationFields = question.questionFields?.find(
                (field) => field.locale === translationLocale
              );
              return (
                <TranslationLine key={question.id}>
                  <QuestionTranslationCard
                    index={index}
                    formLocale={formLocale}
                    control={control}
                    register={register}
                    namePrefix={`${Column.Fields}.questionsFields.${index}`}
                    defaultValue={{
                      id: defaultFields?.id || "",
                      body: defaultFields?.body || "",
                      questionId: question.id,
                      choices: getQuestionChoices(question.choices || [], formLocale),
                    }}
                    bodyErrorMessage={fieldErrors?.["questionsFields"]?.[index]?.["body"]?.message}
                    choiceErrors={fieldErrors?.["questionsFields"]?.[index]?.["choices"] as FieldErrors}
                    isTranslationColumn={false}
                    translationLocale={translationLocale}
                    setValue={setValue}
                  />

                  <QuestionTranslationCard
                    index={index}
                    formLocale={formLocale}
                    control={control}
                    register={register}
                    namePrefix={`${Column.Translations}.questionsFields.${index}`}
                    defaultValue={{
                      id: defaultTranslationFields?.id || v4(),
                      body: defaultTranslationFields?.body || "",
                      questionId: question.id,
                      choices: getQuestionChoices(question.choices || [], translationLocale),
                    }}
                    bodyErrorMessage={translationErrors?.["questionsFields"]?.[index]?.["body"]?.message}
                    choiceErrors={translationErrors?.["questionsFields"]?.[index]?.["choices"] as FieldErrors}
                    isTranslationColumn={true}
                    translationLocale={translationLocale}
                    setValue={setValue}
                    textToTranslate={{
                      id: defaultFields?.id || "",
                      body: defaultFields?.body || "",
                      questionId: question.id,
                      choices: getQuestionChoices(question.choices || [], formLocale),
                    }}
                    isLoading={isTranslatingQuestions && !translatedQuestionFieldIds.has(question.id)}
                  />
                </TranslationLine>
              );
            })}
          </TranslationSection>
        )}
        <CommunicationPreferencesTitleTranslation
          questionTitle={communicationPreferencesTitle}
          savedTranslation={defaultTranslationFields.communicationPreferencesTitle}
          formLocale={formLocale}
          translationLocale={translationLocale}
        />
      </Stack>
    </TranslationLocaleProvider>
  );
};

interface RenewalEmailTranslationFieldsProps {
  defaultFields: TicketingEmailTranslationText;
  defaultTranslationFields: TicketingEmailTranslationText;
  fieldErrors: FieldErrors<TicketingTranslationsData>[Column.Fields];
  translationErrors: FieldErrors<TicketingTranslationsData>[Column.Translations];
  isRequiredRule: ReturnType<typeof isRequired>[];
  control: ReturnType<typeof useFormContext<TicketingTranslationsData>>["control"];
  setValue: ReturnType<typeof useFormContext<TicketingTranslationsData>>["setValue"];
  translationLocale: Locales;
  expiringFieldSubjectRef: React.RefObject<Nullable<HTMLInputElement>>;
  expiringTranslationSubjectRef: React.RefObject<Nullable<HTMLInputElement>>;
  autoRenewalFieldSubjectRef: React.RefObject<Nullable<HTMLInputElement>>;
  autoRenewalTranslationSubjectRef: React.RefObject<Nullable<HTMLInputElement>>;
}

const RenewalEmailTranslationFields: FC<RenewalEmailTranslationFieldsProps> = ({
  defaultFields,
  defaultTranslationFields,
  fieldErrors,
  translationErrors,
  isRequiredRule,
  control,
  setValue,
  translationLocale,
  expiringFieldSubjectRef,
  expiringTranslationSubjectRef,
  autoRenewalFieldSubjectRef,
  autoRenewalTranslationSubjectRef,
}) => {
  const { t } = useTranslate();
  const formLocale = useTranslationLocales()?.formLocale;
  const {
    expiringMembershipEmailObject,
    expiringMembershipEmailContent,
    autoRenewalEmailObject,
    autoRenewalEmailContent,
  } = defaultFields;

  return (
    <>
      {Boolean(expiringMembershipEmailObject || expiringMembershipEmailContent) && (
        <TranslationSection title={t("dashboard", "translation.expiringMembershipEmail")}>
          {expiringMembershipEmailObject && (
            <TranslationLine>
              <DashboardTextField
                label={t("dashboard", "translation.subjectLine", { lng: getTranslationLabelLanguage(formLocale) })}
                rules={isRequiredRule}
                control={control}
                name={`${Column.Fields}.expiringMembershipEmailObject`}
                defaultValue={defaultFields.expiringMembershipEmailObject}
                error={Boolean(fieldErrors && fieldErrors["expiringMembershipEmailObject"])}
                errorMessage={fieldErrors && fieldErrors["expiringMembershipEmailObject"]?.message}
                inputProps={{ ref: expiringFieldSubjectRef }}
                data-test="ticketing-expiringMembershipEmailObject-field"
              />
              <TranslatedTextField
                fieldName={`${Column.Translations}.expiringMembershipEmailObject`}
                defaultFieldValue={defaultFields.expiringMembershipEmailObject}
                defaultTranslationFieldValue={defaultTranslationFields.expiringMembershipEmailObject}
                setValue={setValue}
                translationLocale={translationLocale}
                label={t("dashboard", "translation.subjectLine", {
                  lng: getTranslationLabelLanguage(translationLocale),
                })}
                rules={isRequiredRule}
                control={control}
                inputProps={{ ref: expiringTranslationSubjectRef }}
                data-test="ticketing-expiringMembershipEmailObject-translation"
                error={Boolean(translationErrors && translationErrors["expiringMembershipEmailObject"])}
                errorMessage={translationErrors && translationErrors["expiringMembershipEmailObject"]?.message}
                dataTest="ticketing-expiringMembershipEmailObject-translation"
              />
            </TranslationLine>
          )}
          {expiringMembershipEmailContent && (
            <TranslationLine>
              <TranslationFieldWysiwyg
                name={`${Column.Fields}.expiringMembershipEmailContent`}
                subjectName={`${Column.Fields}.expiringMembershipEmailObject`}
                subjectRef={expiringFieldSubjectRef}
                defaultValue={defaultFields.expiringMembershipEmailContent || undefined}
                rules={isRequiredRule}
                heightMinOver310={400}
                error={fieldErrors && fieldErrors["expiringMembershipEmailContent"]}
                data-test="ticketing-expiringMembershipEmailContent-field"
                config={FroalaConfig.Email}
              />
              <TranslatedFieldWysiwyg
                fieldName={`${Column.Translations}.expiringMembershipEmailContent`}
                defaultFieldValue={defaultFields.expiringMembershipEmailContent}
                defaultTranslationFieldValue={defaultTranslationFields.expiringMembershipEmailContent}
                setValue={setValue}
                translationLocale={translationLocale}
                subjectRef={expiringTranslationSubjectRef}
                rules={isRequiredRule}
                error={translationErrors && translationErrors["expiringMembershipEmailContent"]}
                dataTest="ticketing-expiringMembershipEmailContent-translation"
                config={FroalaConfig.Email}
              />
            </TranslationLine>
          )}
        </TranslationSection>
      )}
      {Boolean(autoRenewalEmailObject || autoRenewalEmailContent) && (
        <TranslationSection title={t("dashboard", "translation.autoRenewalEmail")}>
          {autoRenewalEmailObject && (
            <TranslationLine>
              <DashboardTextField
                label={t("dashboard", "translation.subjectLine", { lng: getTranslationLabelLanguage(formLocale) })}
                rules={isRequiredRule}
                control={control}
                name={`${Column.Fields}.autoRenewalEmailObject`}
                defaultValue={defaultFields.autoRenewalEmailObject}
                error={Boolean(fieldErrors && fieldErrors["autoRenewalEmailObject"])}
                errorMessage={fieldErrors && fieldErrors["autoRenewalEmailObject"]?.message}
                inputProps={{ ref: autoRenewalFieldSubjectRef }}
                data-test="ticketing-autoRenewalEmailObject-field"
              />
              <TranslatedTextField
                fieldName={`${Column.Translations}.autoRenewalEmailObject`}
                defaultFieldValue={defaultFields.autoRenewalEmailObject}
                defaultTranslationFieldValue={defaultTranslationFields.autoRenewalEmailObject}
                setValue={setValue}
                translationLocale={translationLocale}
                label={t("dashboard", "translation.subjectLine", {
                  lng: getTranslationLabelLanguage(translationLocale),
                })}
                rules={isRequiredRule}
                control={control}
                inputProps={{ ref: autoRenewalTranslationSubjectRef }}
                data-test="ticketing-autoRenewalEmailObject-translation"
                error={Boolean(translationErrors && translationErrors["autoRenewalEmailObject"])}
                errorMessage={translationErrors && translationErrors["autoRenewalEmailObject"]?.message}
                dataTest="ticketing-autoRenewalEmailObject-translation"
              />
            </TranslationLine>
          )}
          {autoRenewalEmailContent && (
            <TranslationLine>
              <TranslationFieldWysiwyg
                name={`${Column.Fields}.autoRenewalEmailContent`}
                subjectName={`${Column.Fields}.autoRenewalEmailObject`}
                subjectRef={autoRenewalFieldSubjectRef}
                defaultValue={defaultFields.autoRenewalEmailContent || undefined}
                rules={isRequiredRule}
                heightMinOver310={400}
                error={fieldErrors && fieldErrors["autoRenewalEmailContent"]}
                data-test="ticketing-autoRenewalEmailContent-field"
                config={FroalaConfig.Email}
              />
              <TranslatedFieldWysiwyg
                fieldName={`${Column.Translations}.autoRenewalEmailContent`}
                defaultFieldValue={defaultFields.autoRenewalEmailContent}
                defaultTranslationFieldValue={defaultTranslationFields.autoRenewalEmailContent}
                setValue={setValue}
                translationLocale={translationLocale}
                subjectRef={autoRenewalTranslationSubjectRef}
                rules={isRequiredRule}
                error={translationErrors && translationErrors["autoRenewalEmailContent"]}
                dataTest="ticketing-autoRenewalEmailContent-translation"
                config={FroalaConfig.Email}
              />
            </TranslationLine>
          )}
        </TranslationSection>
      )}
    </>
  );
};

interface FulfillmentEmailTranslationFieldsProps {
  defaultFields: TicketingEmailTranslationText;
  defaultTranslationFields: TicketingEmailTranslationText;
  fieldErrors: FieldErrors<TicketingTranslationsData>[Column.Fields];
  translationErrors: FieldErrors<TicketingTranslationsData>[Column.Translations];
  isRequiredRule: RegisterOptions[];
  control: Control<TicketingTranslationsData>;
  setValue: UseFormSetValue<TicketingTranslationsData>;
  translationLocale: Locales;
  readyForPickupFieldSubjectRef: React.RefObject<Nullable<HTMLInputElement>>;
  readyForPickupTranslationSubjectRef: React.RefObject<Nullable<HTMLInputElement>>;
  orderShippedFieldSubjectRef: React.RefObject<Nullable<HTMLInputElement>>;
  orderShippedTranslationSubjectRef: React.RefObject<Nullable<HTMLInputElement>>;
}

const FulfillmentEmailTranslationFields: FC<FulfillmentEmailTranslationFieldsProps> = ({
  defaultFields,
  defaultTranslationFields,
  fieldErrors,
  translationErrors,
  isRequiredRule,
  control,
  setValue,
  translationLocale,
  readyForPickupFieldSubjectRef,
  readyForPickupTranslationSubjectRef,
  orderShippedFieldSubjectRef,
  orderShippedTranslationSubjectRef,
}) => {
  const { t } = useTranslate();
  const formLocale = useTranslationLocales()?.formLocale;
  const { readyForPickupEmailObject, readyForPickupEmailContent, orderShippedEmailObject, orderShippedEmailContent } =
    defaultFields;

  return (
    <>
      {Boolean(readyForPickupEmailObject || readyForPickupEmailContent) && (
        <TranslationSection title={t("dashboard", "translation.readyForPickupEmail")}>
          {readyForPickupEmailObject && (
            <TranslationLine>
              <DashboardTextField
                label={t("dashboard", "translation.subjectLine", { lng: getTranslationLabelLanguage(formLocale) })}
                rules={isRequiredRule}
                control={control}
                name={`${Column.Fields}.readyForPickupEmailObject`}
                defaultValue={defaultFields.readyForPickupEmailObject}
                error={Boolean(fieldErrors && fieldErrors["readyForPickupEmailObject"])}
                errorMessage={fieldErrors && fieldErrors["readyForPickupEmailObject"]?.message}
                inputProps={{ ref: readyForPickupFieldSubjectRef }}
                data-test="ticketing-readyForPickupEmailObject-field"
              />
              <TranslatedTextField
                fieldName={`${Column.Translations}.readyForPickupEmailObject`}
                defaultFieldValue={defaultFields.readyForPickupEmailObject}
                defaultTranslationFieldValue={defaultTranslationFields.readyForPickupEmailObject}
                setValue={setValue}
                translationLocale={translationLocale}
                label={t("dashboard", "translation.subjectLine", {
                  lng: getTranslationLabelLanguage(translationLocale),
                })}
                rules={isRequiredRule}
                control={control}
                inputProps={{ ref: readyForPickupTranslationSubjectRef }}
                data-test="ticketing-readyForPickupEmailObject-translation"
                error={Boolean(translationErrors && translationErrors["readyForPickupEmailObject"])}
                errorMessage={translationErrors && translationErrors["readyForPickupEmailObject"]?.message}
                dataTest="ticketing-readyForPickupEmailObject-translation"
              />
            </TranslationLine>
          )}
          {readyForPickupEmailContent && (
            <TranslationLine>
              <TranslationFieldWysiwyg
                name={`${Column.Fields}.readyForPickupEmailContent`}
                subjectName={`${Column.Fields}.readyForPickupEmailObject`}
                subjectRef={readyForPickupFieldSubjectRef}
                defaultValue={defaultFields.readyForPickupEmailContent || undefined}
                rules={isRequiredRule}
                heightMinOver310={400}
                error={fieldErrors && fieldErrors["readyForPickupEmailContent"]}
                data-test="ticketing-readyForPickupEmailContent-field"
                config={FroalaConfig.Email}
              />
              <TranslatedFieldWysiwyg
                fieldName={`${Column.Translations}.readyForPickupEmailContent`}
                defaultFieldValue={defaultFields.readyForPickupEmailContent}
                defaultTranslationFieldValue={defaultTranslationFields.readyForPickupEmailContent}
                setValue={setValue}
                translationLocale={translationLocale}
                subjectRef={readyForPickupTranslationSubjectRef}
                rules={isRequiredRule}
                error={translationErrors && translationErrors["readyForPickupEmailContent"]}
                dataTest="ticketing-readyForPickupEmailContent-translation"
                config={FroalaConfig.Email}
              />
            </TranslationLine>
          )}
        </TranslationSection>
      )}
      {Boolean(orderShippedEmailObject || orderShippedEmailContent) && (
        <TranslationSection title={t("dashboard", "translation.orderShippedEmail")}>
          {orderShippedEmailObject && (
            <TranslationLine>
              <DashboardTextField
                label={t("dashboard", "translation.subjectLine", { lng: getTranslationLabelLanguage(formLocale) })}
                rules={isRequiredRule}
                control={control}
                name={`${Column.Fields}.orderShippedEmailObject`}
                defaultValue={defaultFields.orderShippedEmailObject}
                error={Boolean(fieldErrors && fieldErrors["orderShippedEmailObject"])}
                errorMessage={fieldErrors && fieldErrors["orderShippedEmailObject"]?.message}
                inputProps={{ ref: orderShippedFieldSubjectRef }}
                data-test="ticketing-orderShippedEmailObject-field"
              />
              <TranslatedTextField
                fieldName={`${Column.Translations}.orderShippedEmailObject`}
                defaultFieldValue={defaultFields.orderShippedEmailObject}
                defaultTranslationFieldValue={defaultTranslationFields.orderShippedEmailObject}
                setValue={setValue}
                translationLocale={translationLocale}
                label={t("dashboard", "translation.subjectLine", {
                  lng: getTranslationLabelLanguage(translationLocale),
                })}
                rules={isRequiredRule}
                control={control}
                inputProps={{ ref: orderShippedTranslationSubjectRef }}
                data-test="ticketing-orderShippedEmailObject-translation"
                error={Boolean(translationErrors && translationErrors["orderShippedEmailObject"])}
                errorMessage={translationErrors && translationErrors["orderShippedEmailObject"]?.message}
                dataTest="ticketing-orderShippedEmailObject-translation"
              />
            </TranslationLine>
          )}
          {orderShippedEmailContent && (
            <TranslationLine>
              <TranslationFieldWysiwyg
                name={`${Column.Fields}.orderShippedEmailContent`}
                subjectName={`${Column.Fields}.orderShippedEmailObject`}
                subjectRef={orderShippedFieldSubjectRef}
                defaultValue={defaultFields.orderShippedEmailContent || undefined}
                rules={isRequiredRule}
                heightMinOver310={400}
                error={fieldErrors && fieldErrors["orderShippedEmailContent"]}
                data-test="ticketing-orderShippedEmailContent-field"
                config={FroalaConfig.Email}
              />
              <TranslatedFieldWysiwyg
                fieldName={`${Column.Translations}.orderShippedEmailContent`}
                defaultFieldValue={defaultFields.orderShippedEmailContent}
                defaultTranslationFieldValue={defaultTranslationFields.orderShippedEmailContent}
                setValue={setValue}
                translationLocale={translationLocale}
                subjectRef={orderShippedTranslationSubjectRef}
                rules={isRequiredRule}
                error={translationErrors && translationErrors["orderShippedEmailContent"]}
                dataTest="ticketing-orderShippedEmailContent-translation"
                config={FroalaConfig.Email}
              />
            </TranslationLine>
          )}
        </TranslationSection>
      )}
    </>
  );
};

interface BidPlacedEmailTranslationFieldsProps {
  defaultFields: TicketingEmailTranslationText;
  defaultTranslationFields: TicketingEmailTranslationText;
  fieldErrors: FieldErrors<TicketingTranslationsData>[Column.Fields];
  translationErrors: FieldErrors<TicketingTranslationsData>[Column.Translations];
  isRequiredRule: RegisterOptions[];
  control: Control<TicketingTranslationsData>;
  setValue: UseFormSetValue<TicketingTranslationsData>;
  translationLocale: Locales;
  hasDate: boolean;
  bidPlacedSubjectRef: React.RefObject<Nullable<HTMLInputElement>>;
  bidPlacedTranslationSubjectRef: React.RefObject<Nullable<HTMLInputElement>>;
}

const BidPlacedEmailTranslationFields: FC<BidPlacedEmailTranslationFieldsProps> = ({
  defaultFields,
  defaultTranslationFields,
  fieldErrors,
  translationErrors,
  isRequiredRule,
  control,
  setValue,
  translationLocale,
  hasDate,
  bidPlacedSubjectRef,
  bidPlacedTranslationSubjectRef,
}) => {
  const { t } = useTranslate();
  const formLocale = useTranslationLocales()?.formLocale;
  const { thankYouBidPlacedEmailObject, thankYouBidPlacedEmailContent } = defaultFields;

  return (
    <>
      {Boolean(thankYouBidPlacedEmailObject || thankYouBidPlacedEmailContent) && (
        <TranslationSection title={t("dashboard", "translation.bidPlacedEmail")}>
          {thankYouBidPlacedEmailObject && (
            <TranslationLine>
              <DashboardTextField
                label={t("dashboard", "translation.subjectLine", { lng: getTranslationLabelLanguage(formLocale) })}
                rules={isRequiredRule}
                control={control}
                name={`${Column.Fields}.thankYouBidPlacedEmailObject`}
                defaultValue={thankYouBidPlacedEmailObject}
                error={Boolean(fieldErrors && fieldErrors["thankYouBidPlacedEmailObject"])}
                errorMessage={fieldErrors && fieldErrors["thankYouBidPlacedEmailObject"]?.message}
                inputProps={{
                  ref: bidPlacedSubjectRef,
                }}
                data-test="ticketing-thankYouBidPlacedEmailObject-field"
              />
              <TranslatedTextField
                fieldName={`${Column.Translations}.thankYouBidPlacedEmailObject`}
                defaultFieldValue={thankYouBidPlacedEmailObject}
                defaultTranslationFieldValue={defaultTranslationFields.thankYouBidPlacedEmailObject}
                setValue={setValue}
                translationLocale={translationLocale}
                label={t("dashboard", "translation.subjectLine", {
                  lng: getTranslationLabelLanguage(translationLocale),
                })}
                rules={isRequiredRule}
                control={control}
                inputProps={{
                  ref: bidPlacedTranslationSubjectRef,
                }}
                data-test="ticketing-thankYouBidPlacedEmailObject-translation"
                error={Boolean(translationErrors && translationErrors["thankYouBidPlacedEmailObject"])}
                errorMessage={translationErrors && translationErrors["thankYouBidPlacedEmailObject"]?.message}
                dataTest="ticketing-thankYouBidPlacedEmailObject-translation"
              />
            </TranslationLine>
          )}
          {thankYouBidPlacedEmailContent && (
            <TranslationLine>
              <TranslationFieldWysiwyg
                name={`${Column.Fields}.thankYouBidPlacedEmailContent`}
                subjectName={`${Column.Fields}.thankYouBidPlacedEmailObject`}
                subjectRef={bidPlacedSubjectRef}
                defaultValue={thankYouBidPlacedEmailContent}
                rules={isRequiredRule}
                heightMinOver310={400}
                error={fieldErrors && fieldErrors["thankYouBidPlacedEmailContent"]}
                data-test="ticketing-thankYouBidPlacedEmailContent-field"
                customConfig={{
                  date: hasDate,
                }}
                config={FroalaConfig.Email}
              />
              <TranslatedFieldWysiwyg
                fieldName={`${Column.Translations}.thankYouBidPlacedEmailContent`}
                defaultFieldValue={thankYouBidPlacedEmailContent}
                defaultTranslationFieldValue={defaultTranslationFields.thankYouBidPlacedEmailContent}
                setValue={setValue}
                translationLocale={translationLocale}
                subjectRef={bidPlacedTranslationSubjectRef}
                rules={isRequiredRule}
                error={translationErrors && translationErrors["thankYouBidPlacedEmailContent"]}
                dataTest="ticketing-thankYouBidPlacedEmailContent-translation"
                customConfig={{
                  date: hasDate,
                }}
                config={FroalaConfig.Email}
              />
            </TranslationLine>
          )}
        </TranslationSection>
      )}
    </>
  );
};

interface ReminderEmailTranslationFieldsProps {
  reminders: ReminderTranslationDefaults[];
  fieldErrors: FieldErrors<TicketingTranslationsData>[Column.Fields];
  translationErrors: FieldErrors<TicketingTranslationsData>[Column.Translations];
  isRequiredRule: ReturnType<typeof isRequired>[];
  control: ReturnType<typeof useFormContext<TicketingTranslationsData>>["control"];
  setValue: ReturnType<typeof useFormContext<TicketingTranslationsData>>["setValue"];
  translationLocale: Locales;
  hasDate: boolean;
}

const ReminderEmailTranslationFields: FC<ReminderEmailTranslationFieldsProps> = ({ reminders, ...sharedProps }) => (
  <>
    {reminders.map((reminder) => (
      <ReminderEmailTranslationLine key={reminder.key} reminder={reminder} {...sharedProps} />
    ))}
  </>
);

const ReminderEmailTranslationLine: FC<
  Omit<ReminderEmailTranslationFieldsProps, "reminders"> & { reminder: ReminderTranslationDefaults }
> = ({ reminder, fieldErrors, translationErrors, isRequiredRule, control, setValue, translationLocale, hasDate }) => {
  const { t } = useTranslate();
  const formLocale = useTranslationLocales()?.formLocale;
  const subjectRef = React.useRef<Nullable<HTMLInputElement>>(null);
  const translationSubjectRef = React.useRef<Nullable<HTMLInputElement>>(null);
  const label = t("dashboard", `translation.${reminder.key}Email`);
  const reminderFieldErrors = fieldErrors?.reminders?.[reminder.key];
  const reminderTranslationErrors = translationErrors?.reminders?.[reminder.key];

  const isSwapped = useTranslationSwapVersion() % 2 === 1;
  const [mainSide, translatedSide] = isSwapped
    ? [reminder.translations, reminder.fields]
    : [reminder.fields, reminder.translations];

  return (
    <TranslationSection title={label}>
      <TranslationLine>
        <DashboardTextField
          label={t("dashboard", "translation.subjectLine", { lng: getTranslationLabelLanguage(formLocale) })}
          rules={isRequiredRule}
          control={control}
          name={`${Column.Fields}.reminders.${reminder.key}.subject`}
          defaultValue={mainSide.subject}
          error={Boolean(reminderFieldErrors?.subject)}
          errorMessage={reminderFieldErrors?.subject?.message}
          inputProps={{ ref: subjectRef }}
          data-test={`ticketing-${reminder.key}-subject-field`}
        />
        <TranslatedTextField
          fieldName={`${Column.Translations}.reminders.${reminder.key}.subject`}
          defaultFieldValue={mainSide.subject}
          defaultTranslationFieldValue={translatedSide.subject}
          setValue={setValue}
          translationLocale={translationLocale}
          label={t("dashboard", "translation.subjectLine", { lng: getTranslationLabelLanguage(translationLocale) })}
          control={control}
          inputProps={{ ref: translationSubjectRef }}
          error={Boolean(reminderTranslationErrors?.subject)}
          errorMessage={reminderTranslationErrors?.subject?.message}
          dataTest={`ticketing-${reminder.key}-subject-translation`}
          skipTranslation={isSwapped}
        />
      </TranslationLine>
      <TranslationLine>
        <TranslationFieldWysiwyg
          name={`${Column.Fields}.reminders.${reminder.key}.body`}
          subjectName={`${Column.Fields}.reminders.${reminder.key}.subject`}
          subjectRef={subjectRef}
          defaultValue={mainSide.body}
          rules={isRequiredRule}
          heightMinOver310={400}
          error={reminderFieldErrors?.body}
          data-test={`ticketing-${reminder.key}-body-field`}
          customConfig={{ date: hasDate }}
          config={FroalaConfig.Email}
        />
        <TranslatedFieldWysiwyg
          fieldName={`${Column.Translations}.reminders.${reminder.key}.body`}
          defaultFieldValue={mainSide.body}
          defaultTranslationFieldValue={translatedSide.body}
          setValue={setValue}
          translationLocale={translationLocale}
          subjectRef={translationSubjectRef}
          error={reminderTranslationErrors?.body}
          dataTest={`ticketing-${reminder.key}-body-translation`}
          customConfig={{ date: hasDate }}
          config={FroalaConfig.Email}
          skipTranslation={isSwapped}
        />
      </TranslationLine>
    </TranslationSection>
  );
};

const rateSectionLabels = {
  [TicketingFormCategory.Event]: "translation.tickets",
  [TicketingFormCategory.Lottery]: "translation.tickets",
  [TicketingFormCategory.Sweepstakes]: "sweepstakes.navigation.entries",
  [TicketingFormCategory.Custom]: "translation.tickets",
  [TicketingFormCategory.MembershipV2]: "translation.memberships",
  [TicketingFormCategory.Shop]: "translation.products",
  [TicketingFormCategory.Auction]: "translation.items",
} as const;

const hasCampaignDetails = (title: string | null | undefined, description: string | null | undefined) =>
  Boolean(title) || typeof description === "string";

const getPaymentInstructionsVisibility = (
  isAuction: boolean,
  fields: { chequeDescription?: string | null; offlinePaymentWording?: string | null }
) => ({
  showChequeDescription: !isAuction && Boolean(fields.chequeDescription),
  showOfflinePaymentWording: isAuction && Boolean(fields.offlinePaymentWording),
});
